import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useTranslation } from 'react-i18next';
import { WindowData } from '@/api/mentalHealth/report.types';
import { useAppLocale } from '@/locales/locale';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

interface VitalSignsChartProps {
  windows: WindowData[];
  type: 'hr' | 'rmssd' | 'resp' | 'dqi';
  className?: string;
}

const VitalSignsChart: React.FC<VitalSignsChartProps> = ({ 
  windows, 
  type, 
  className = '' 
}) => {
  const { t } = useTranslation();
  const { intlLocale } = useAppLocale();
  
  const getChartConfig = (type: string) => {
    switch (type) {
      case 'hr':
        return {
          title: t('mentalHealth.detail.heartRateChart'),
          yLabel: 'BPM',
          code: 'HR',
          description: t('mentalHealth.vitalChart.hr.desc'),
          color: 'rgba(229, 65, 85, 0.12)',
          borderColor: '#e54155',
          dataKey: 'HR_bpm' as keyof WindowData,
        };
      case 'rmssd':
        return {
          title: 'RMSSD',
          yLabel: 'ms',
          code: 'RV',
          description: t('mentalHealth.vitalChart.rmssd.desc'),
          color: 'rgba(24, 168, 120, 0.12)',
          borderColor: '#18a878',
          dataKey: 'RMSSD_ms' as keyof WindowData,
        };
      case 'resp':
        return {
          title: t('mentalHealth.detail.respirationChart'),
          yLabel: t('mentalHealth.reportDetail.rpm'),
          code: 'BR',
          description: t('mentalHealth.vitalChart.resp.desc'),
          color: 'rgba(41, 102, 244, 0.12)',
          borderColor: '#2966f4',
          dataKey: 'Resp_bpm' as keyof WindowData,
        };
      case 'dqi':
        return {
          title: t('mentalHealth.detail.dqiChart'),
          yLabel: 'Quality (0-1)',
          code: 'DQ',
          description: t('mentalHealth.vitalChart.dqi.desc'),
          color: 'rgba(119, 102, 218, 0.12)',
          borderColor: '#7766da',
          dataKey: 'DQI' as keyof WindowData,
        };
      default:
        return {
          title: t('common.data'),
          yLabel: 'Value',
          code: 'DT',
          description: '',
          color: 'rgba(107, 114, 128, 0.12)',
          borderColor: '#6b7280',
          dataKey: 'HR_bpm' as keyof WindowData,
        };
    }
  };

  const config = getChartConfig(type);
  
  // 유효한 데이터만 필터링
  const validWindows = windows.filter(w => {
    const value = w[config.dataKey];
    // 서버 리포트는 JSON 이라 값이 부족하면 null 이다. 전역 isFinite 는 isFinite(null) === true 라
    // null 을 전부 통과시킨다 — 반드시 Number.isFinite 를 쓴다.
    if (!Number.isFinite(value)) return false;
    return type !== 'dqi' || (Number.isFinite(w.DQI) && w.DQI! >= 0.3); // DQI는 최소 0.3 이상만 표시
  });

  const chartData = {
    labels: validWindows.map(w => w.t_start),
    datasets: [
      {
        label: config.title,
        data: validWindows.map(w => w[config.dataKey] as number),
        backgroundColor: config.color,
        borderColor: config.borderColor,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        pointHoverRadius: 5,
        pointBorderWidth: 2,
        pointBorderColor: '#ffffff',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#071a3d',
        padding: 12,
        cornerRadius: 10,
        callbacks: {
          title: (context: any) => {
            const date = new Date(context[0].label);
            return date.toLocaleString(intlLocale);
          },
          label: (context: any) => {
            return `${config.title}: ${context.parsed.y.toFixed(2)} ${config.yLabel}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        title: {
          display: false,
          text: t('mentalHealth.detail.timeLabel'),
        },
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#7a879a', font: { size: 11 } },
      },
      y: {
        beginAtZero: type === 'dqi',
        title: {
          display: false,
          text: config.yLabel,
        },
        grid: { color: 'rgba(112, 132, 164, 0.12)' },
        border: { display: false },
        ticks: { color: '#7a879a', font: { size: 11 } },
        ...(type === 'dqi' && {
          min: 0,
          max: 1,
        }),
      },
    },
  };

  if (validWindows.length === 0) {
    return (
      <div data-pdf-block className={`vital-chart-card vital-chart-card--empty ${className}`}>
        <div>
          <span>{config.code}</span>
          <strong>{config.title}</strong>
          <p>{t('common.noData')}</p>
        </div>
      </div>
    );
  }

  return (
    <article data-pdf-block className={`vital-chart-card vital-chart-card--${type} ${className}`}>
      <header className="vital-chart-card__header">
        <span>{config.code}</span>
        <div>
          <h3>{config.title}</h3>
          <p>{config.description}</p>
        </div>
      </header>
      <div className="vital-chart-card__canvas">
        <Line data={chartData} options={options} />
      </div>
      
      <div className="vital-chart-card__stats">
        <div>
          <span>{t('mentalHealth.detail.average')}</span>
          <strong>{(validWindows.reduce((sum, w) => sum + (w[config.dataKey] as number), 0) / validWindows.length).toFixed(2)}</strong>
        </div>
        <div>
          <span>{t('mentalHealth.detail.minimum')}</span>
          <strong>{Math.min(...validWindows.map(w => w[config.dataKey] as number)).toFixed(2)}</strong>
        </div>
        <div>
          <span>{t('mentalHealth.detail.maximum')}</span>
          <strong>{Math.max(...validWindows.map(w => w[config.dataKey] as number)).toFixed(2)}</strong>
        </div>
        <small>{config.yLabel}</small>
      </div>
    </article>
  );
};

export default VitalSignsChart;
